import Phaser from 'phaser';
import { Planet, PlanetConfig } from './Planet';
import { Toggle } from '../controls/forms/Toggle';
import { ListBox } from '../controls/forms/ListBox';
import { Popup } from '../controls/overlays/Popup';
import { Anchor } from '../controls/Anchor';
import { UIConfig } from '../controls/UIConfig';
import { DropDownList } from '../controls/forms/DropDownList';
import { Theme } from '../controls/Theme';
import { SceneHelper } from '../utils/SceneHelper';
import { Button } from '../controls/buttons/Button';


export class PlanetScene extends Phaser.Scene {
    graphics!: Phaser.GameObjects.Graphics;

    // ui
    private btnPlanet!: Toggle;
    private btnForce!: Toggle;
    private btnTrail!: Toggle;
    private btnTrace!: Toggle;
    private lblTimer!: Phaser.GameObjects.Text;
    private lblName!: Phaser.GameObjects.Text;
    private imgBg!: Phaser.GameObjects.Image;
    private uiContainer!: Phaser.GameObjects.Container;

    // parameters
    name!: string;
    position!: Phaser.Math.Vector2;
    zoom!: number;
    showOwn: boolean = false;    // 是否显示星球信息
    showForce: boolean = false;  // 是否显示引力
    showTrail: boolean = true;   // 是否显示轨迹
    showTrace: boolean = false;  // 是否跟踪选中星球
    cfgs!: PlanetConfig[];       // 星球配置信息
    minZoom: number = 0.1;       // 最小缩放比例
    maxZoom: number = 4.0;       // 最大缩放比例
    
    // data
    startTime!: number;
    isDragging: boolean = false;
    dragStartX: number = 0;
    dragStartY: number = 0;
    planets!: Planet[];
    ddlPlanets!: DropDownList;

    // 双指缩放相关
    private pointers: Map<number, Phaser.Input.Pointer> = new Map();
    private initialDistance: number = 0;
    lblZoom: any;


    constructor() {
        super('PlanetScene');
    }

    // 接受场景参数
    init(data: any) {
        this.name = data.name;
        this.position = data.position || new Phaser.Math.Vector2(0, 0);
        this.zoom = data.zoom || 1;
        this.showOwn = data.showOwn || false;
        this.showForce = data.showForce || false;
        this.showTrail = data.showTrail || true;
        this.cfgs = data.planets;
    }

    preload() {
        //SceneHelper.showLoading(this); // 资源很少加载很快，这个方法可以不调用
        this.load.image('planet', 'assets/planet.svg');
        this.load.image('space-bg', 'assets/galaxy.jpg');
        this.load.image('back', 'assets/icons/back.svg');
        this.load.image('plus', 'assets/icons/plus.svg');
        this.load.image('minus', 'assets/icons/minus.svg');
        this.load.image('layers', 'assets/icons/layers.svg');
        this.load.image(UIConfig.icons.close.key, UIConfig.icons.close.path);
        this.load.image(UIConfig.icons.down.key, UIConfig.icons.down.path);
    }


    //-------------------------------------------------
    // 初始化
    //-------------------------------------------------
    create() {
        //var camera = this.cameras.add(0, 0, 1, 1).setBackgroundColor(ThemeManager.current.color.bgDark);
        //camera.setPostPipelineData()
        this.graphics = this.add.graphics();

        // 背景
        var w = this.scale.width;
        var h = this.scale.height;
        this.imgBg = this.add.image(w / 2, h / 2, 'space-bg').setDepth(-1).setScrollFactor(0);
        this.imgBg.setDisplaySize(w*1.5, h*1.5);
        this.tweens.add({
            targets: this.imgBg,
            //angle: 360,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 50000,
            ease: 'Linear', // 'Sine.easeInOut',
            yoyo: true,
            repeat: -1
        });

        // planets
        this.planets = this.cfgs.map((cfg: any) => new Planet(this, cfg));
        this.startTime = this.time.now;
    
        // 创建UI容器，所有UI控件都加到这个容器里
        this.uiContainer = this.add.container(0, 0);
        this.uiContainer.setScrollFactor(0);
    
        // title: back, name, timer
        //var btnBack = this.add.image(20, 30, 'back').setScale(1.5).setInteractive()
        //    .on('pointerdown', () => this.scene.start('WelcomeScene')).setOrigin(0.5).setScrollFactor(0);
        var btnBack = new Button(this, 10, 30, '', {icon:'back', mode:'text', width:32, height:32, radius:16, iconWidth:28, iconHeight:30, bgColor: Theme.current.color.primary})
           .setOrigin(0, 0.5)
           .onClick(() => this.scene.start('WelcomeScene'));

        this.lblName  = this.add.text(50, 30, this.name, { font: '24px Arial', color: '#ffffff' }).setOrigin(0, 0.5);
        this.lblTimer = this.add.text(this.scale.width - 80, 30, '00:00:00', { font: '22px Arial', color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', padding: { x: 10, y: 6 } })
            .setDepth(10).setOrigin(0.5)
            .setInteractive()
            .on('pointerdown', () => this.reboot())
            ;
        this.uiContainer.add(btnBack);
        this.uiContainer.add(this.lblName);
        this.uiContainer.add(this.lblTimer);

        // zoom, toggle
        this.createZoomButtons();
        this.createToggleButtons();
        this.createPlanetButton();

        // events
        this.setEvents();
    }

    /**重启场景 */
    reboot(){
        this.zoom = 1;
        this.position = new Phaser.Math.Vector2(0, 0);
        this.planets.forEach(planet => planet.destroy());
        this.planets = this.cfgs.map((cfg: any) => new Planet(this, cfg));
        this.startTime = this.time.now;
        this.showTimer();
    } 


    /**自适应屏幕大小变更 */
    resize(gameSize: Phaser.Structs.Size) {
        const width = gameSize.width;
        const height = gameSize.height;
        const centerX = width / 2;
        const centerY = height - 50;
        this.imgBg.setPosition(width / 2, height / 2);
        this.imgBg.displayWidth = width*2;
        this.imgBg.displayHeight = height*2;
        this.lblTimer.setPosition(width - 80, 30);
        this.btnPlanet.setPosition(centerX - 70, centerY);
        this.btnForce.setPosition(centerX, centerY);
        this.btnTrail.setPosition(centerX + 70, centerY);
        this.btnTrace.setPosition(centerX + 140, centerY);
        this.ddlPlanets.setPosition(80, height - 40);
    }

    /**显示时间*/
    private showTimer() {
        if (this.lblTimer) {
            const elapsed = Math.floor((this.time.now - this.startTime) / 1000);
            const hours = Math.floor(elapsed / 3600).toString().padStart(2, '0');
            const minutes = Math.floor((elapsed % 3600) / 60).toString().padStart(2, '0');
            const seconds = (elapsed % 60).toString().padStart(2, '0');
            const fps = Math.floor(this.game.loop.actualFps);
            this.lblTimer.setText(`${hours}:${minutes}:${seconds} ${fps}`);
        }
    }

    //-------------------------------------------------
    // 地图控制
    //-------------------------------------------------
    /** 创建缩放按钮 */
    private createZoomButtons() {
        const centerX = this.game.canvas.width / 2;
        const centerY = this.game.canvas.height / 2;
        const btnSize = 32;
        const margin = 10;


        //const btnPlus = this.add.image(margin + btnSize / 2, 70, 'plus').setInteractive().on('pointerdown', () => {
        //    this.zoom = Phaser.Math.Clamp(this.zoom + 0.1, this.minZoom, this.maxZoom);
        //    this.lblZoom.setText(this.zoom.toFixed(1));
        //    this.position.x *= this.zoom;
        //    this.position.y *= this.zoom;
        //    this.planets.forEach(plane => plane.setZoom(this.zoom, new Phaser.Math.Vector2(centerX, centerY)));
        //});
        const btnPlus = new Button(this, margin, 90, '', {icon:'plus', mode:'frame', width:btnSize, height:btnSize, borderColor:0xffffff, radius:2})
            .setOrigin(0, 0)
            .onClick(() => {
                this.zoom = Phaser.Math.Clamp(this.zoom + 0.1, this.minZoom, this.maxZoom);
                this.lblZoom.setText(this.zoom.toFixed(1));
                this.position.x *= this.zoom;
                this.position.y *= this.zoom;
                this.planets.forEach(plane => plane.setZoom(this.zoom, new Phaser.Math.Vector2(centerX, centerY)));
        });
    
        //const btnMinus = this.add.image(margin + btnSize / 2, 110, 'minus').setInteractive().on('pointerdown', () => {
        //    this.zoom = Phaser.Math.Clamp(this.zoom - 0.1, this.minZoom, this.maxZoom);
        //    this.lblZoom.setText(this.zoom.toFixed(1));
        //    this.position.x *= this.zoom;
        //    this.position.y *= this.zoom;
        //    this.planets.forEach(plane => plane.setZoom(this.zoom, new Phaser.Math.Vector2(centerX, centerY)));
        //});
        const btnMinus = new Button(this, margin, 125, '', {icon:'minus', mode:'frame', width:btnSize, height:btnSize, borderColor:0xffffff, radius:2})
            .setOrigin(0, 0)
            .onClick(() => {
                this.zoom = Phaser.Math.Clamp(this.zoom - 0.1, this.minZoom, this.maxZoom);
                this.lblZoom.setText(this.zoom.toFixed(1));
                this.position.x *= this.zoom;
                this.position.y *= this.zoom;
                this.planets.forEach(plane => plane.setZoom(this.zoom, new Phaser.Math.Vector2(centerX, centerY)));
           });
    
        this.lblZoom = this.add.text(margin + btnSize / 2, 180, this.zoom.toFixed(1), {
            font: '16px Arial',
            color: '#ffffff',
            //backgroundColor: 'rgba(0,0,0,0.5)',
            padding: { x: 8, y: 2 },
            align: 'center'
        }).setOrigin(0.5).setSize(btnSize, btnSize);
    
        this.uiContainer.add(this.lblZoom);
        this.uiContainer.add(btnPlus);
        this.uiContainer.add(btnMinus);
    }

    /** Create toggle buttons */
    createToggleButtons() {
        const centerX = this.scale.width / 2;
        const centerY = this.scale.height - 50;

        this.btnPlanet = new Toggle(this, centerX - 70, centerY, '● 星球  ', '○ 星球  ', this.showOwn, (value) => {
            this.planets.forEach(plane => plane.showOwn = value);
        });

        this.btnForce = new Toggle(this, centerX, centerY, '● 引力  ', '○ 引力  ', this.showForce, (value) => {
            this.planets.forEach(plane => plane.showForce = value);
        });

        this.btnTrail = new Toggle(this, centerX + 70, centerY, '● 轨迹  ', '○ 轨迹  ', this.showTrail, (value) => {
            this.planets.forEach(plane => plane.showTail = value);
        });
        this.btnTrace = new Toggle(this, centerX + 140, centerY, '● 跟踪  ', '○ 跟踪  ', this.showTrace, (value) => {
            this.showTrace = value;
        });

        this.uiContainer.add(this.btnPlanet);
        this.uiContainer.add(this.btnForce);
        this.uiContainer.add(this.btnTrail);
        this.uiContainer.add(this.btnTrace);
    }

    //-------------------------------------------------
    // 星球列表
    //-------------------------------------------------
    /**创建星球列表，点击后可将该星球居中 */
    createPlanetButton(){
        this.ddlPlanets = new DropDownList(this, {
            x: 70,
            y: this.game.canvas.height - 40,
            width: 110,
            height: 30,
            bgColor: 0x010101,
            textColor: 0xffffff,
            selectTextColor: 0xffff00,
            iconKey: UIConfig.icons.down.key,
            fontSize: '18px',
            borderWidth: 1,
        })
        .setAlpha(0.8).setOrigin(0)
        .bind(this.cfgs, 'name', 'name')
        .onChanged((_, item: PlanetConfig) => {
            var name = item.name;
            var planet = this.planets.find(planet => planet.name === name);
            if (planet) {
                this.planets.forEach(p => p.selected = (p === planet));
                this.moveTo(planet);
            }
        })
    }

    //-------------------------------------------------
    // 事件
    //-------------------------------------------------
    private setEvents() {
        this.scale.on('resize', this.resize, this);

        this.pointers.clear();
        this.input.addPointer(2);

        // 处理触控点按下事件
        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            this.pointers.set(pointer.id, pointer);

            if (this.pointers.size === 2) {
                // 双指操作：记录初始距离
                const pointers = Array.from(this.pointers.values());
                this.initialDistance = Phaser.Math.Distance.Between(
                    pointers[0].x, pointers[0].y,
                    pointers[1].x, pointers[1].y
                );
            } else if (this.pointers.size === 1) {
                // 单指操作：开始拖拽
                this.isDragging = true;
                this.dragStartX = pointer.x;
                this.dragStartY = pointer.y;
            }
        });

        // 处理触控点移动事件
        this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
            if (this.pointers.size === 2) {
                // 双指缩放
                const pointers = Array.from(this.pointers.values());
                const currentDistance = Phaser.Math.Distance.Between(
                    pointers[0].x, pointers[0].y,
                    pointers[1].x, pointers[1].y
                );
                const pinchCenter = new Phaser.Math.Vector2(
                    (pointers[0].x + pointers[1].x) / 2,
                    (pointers[0].y + pointers[1].y) / 2
                );

                if (this.initialDistance > 0) {
                    // 计算新的缩放值
                    const scaleFactor = currentDistance / this.initialDistance;
                    const zoomDelta = (scaleFactor - 1) * 0.1;
                    const newZoom = Phaser.Math.Clamp(this.zoom + zoomDelta, this.minZoom, this.maxZoom);
                    if (newZoom !== this.zoom) {
                        this.zoom = newZoom;
                        //this.position.x *= this.zoom;
                        //this.position.y *= this.zoom;
                        this.planets.forEach(plane => plane.setZoom(this.zoom, pinchCenter));
                        this.lblZoom.setText(this.zoom.toFixed(2));
                    }
                }
                this.initialDistance = currentDistance;
            } else if (this.isDragging) {
                // 单指拖拽
                const dx = pointer.x - this.dragStartX;
                const dy = pointer.y - this.dragStartY;
                this.offsetPlanets(dx, dy);
                this.dragStartX = pointer.x;
                this.dragStartY = pointer.y;
            }
        });

        // 处理触控点抬起和离开事件
        this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
            this.pointers.delete(pointer.id);
            if (this.pointers.size === 0) {
                this.isDragging = false;
                this.initialDistance = 0;
            }
        });

        this.input.on('pointerout', (pointer: Phaser.Input.Pointer) => {
            this.pointers.delete(pointer.id);
            if (this.pointers.size === 0) {
                this.isDragging = false;
                this.initialDistance = 0;
            }
        });
    }

    /**移动地图，将某个星球位于地图中央 */
    moveTo(planet: Planet) {
        const centerX = this.cameras.main.width / 2;
        const centerY = this.cameras.main.height / 2;
        const dx = planet.sprite.x - centerX;
        const dy = planet.sprite.y - centerY;

        //this.cameras.main.pan(planet.sprite.x, planet.sprite.y, 1000, 'Linear');
        this.offsetPlanets(-dx, -dy);
    }


    /**移动所有星球一个偏移量 */
    private offsetPlanets(dx: number, dy: number) {
        this.position.x -= dx;
        this.position.y -= dy;
        this.planets.forEach(plane => plane.offset(dx, dy));
    }

    //-------------------------------------------------
    // 绘制
    //-------------------------------------------------
    update() {
        this.showTimer();

        // 选中的星球跟踪居中
        if (this.showTrace) {
            var selectPanet = this.planets.find(planet => planet.selected);
            if (selectPanet)
                this.moveTo(selectPanet);
        }

        // 计算和绘制各星球
        this.graphics.clear();
        if (this.planets.length >= 2) {
            // 计算所有两两组合的引力并叠加到各自星球
            const forces = this.planets.map(() => ({ x: 0, y: 0 }));
            for (let i = 0; i < this.planets.length; i++) {
                var planet = this.planets[i];

                // 计算引力合力
                for (let j = 0; j < this.planets.length; j++) {
                    if (i !== j) {
                        var otherPlanet = this.planets[j];
                        const {force, forceX, forceY } = this.calcForce(planet, otherPlanet);
                        forces[i].x += forceX;
                        forces[i].y += forceY;
                        //var visible = (forceX > 0.001 || forceY > 0.001);
                        var visible = force > 0.001;
                        planet.drawForce(this.graphics, forceX, forceY, otherPlanet, visible);  // 绘制与某个星球的引力信息
                    }
                }
                
                // 根据引力合力更新星球速度
                if (!planet.fix) {
                    planet.sprite.body!.velocity.x += forces[i].x / planet.mass;
                    planet.sprite.body!.velocity.y += forces[i].y / planet.mass;
                }

                // 绘制星球信息和轨迹
                planet.drawOwn(this.graphics);
                planet.drawTrail(this.graphics);
            }
        }

    }

    /**
     * 计算两个星球之间的引力
     * @param planet1 星球1
     * @param planet2 星球2
     * @param G 引力常数
     * @returns 引力
     */
    private calcForce(planet1: Planet, planet2: Planet, G: number = 20000): { force:number, forceX: number, forceY: number } {
        const dx = planet2.sprite.x - planet1.sprite.x;
        const dy = planet2.sprite.y - planet1.sprite.y;
        let distanceSq = dx * dx + dy * dy;
        let distance = Math.sqrt(distanceSq);
        const force = G * planet1.mass * planet2.mass / distanceSq; // 引力=引力常数*质量1*质量2/距离的平方
        const forceX = force * dx / distance; // x 方向的引力
        const forceY = force * dy / distance; // y 方向的引力
        return { force, forceX, forceY };
    }


}




